import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
  video,
} from '../helpers';

export const gettingStartedGuides = [
  buildGuide({
    id: 'welcome',
    title: 'Welcome to Roomnexa',
    moduleId: 'getting-started',
    summary:
      'Learn what RoomNexa PMS is, who it is for, and how hotel staff use it day to day.',
    keywords: ['welcome', 'pms', 'overview', 'roomnexa', 'getting started'],
    pathHint: 'Landing → User Guide',
    related: ['first-login', 'property-setup', 'dashboard-home'],
    videos: [
      video(
        'oHYUPB2G_S0',
        'Roomnexa PMS overview',
        'A short product walkthrough of the Roomnexa PMS platform.'
      ),
    ],
    overview: [
      ...paragraphs(
        'RoomNexa is a cloud Property Management System (PMS) built for hotels, resorts, boutique properties, and multi-hotel groups. It brings front office, housekeeping, restaurant/POS, stock, finance, reports, guest CRM, online distribution, and direct booking (NexaStay) into one product so staff do not bounce between spreadsheets and separate apps.',
        'This User Guide is written for people who run the hotel floor: receptionists, cashiers, housekeeping supervisors, F&B staff, revenue managers, and property or group managers. Each page maps to a real RoomNexa screen, with permissions, steps, field notes, and troubleshooting you can use during a live shift.',
        'Day-to-day work usually happens after you open a hotel from Hotelier. Organisation-level screens (multi-hotel dashboard, users, subscription) sit on the Landing page for owners and admins. Property-level screens (bookings, rooms, POS, reports) appear only after you select the correct hotel.',
        'A typical boutique example: morning shift opens Hotelier → Dashboard, clears pending check-ins and checkouts, takes walk-ins from the calendar, updates housekeeping, settles restaurant room charges before checkout, then closes cash. A group GM might start on Organisation Dashboard to compare occupancy across properties, then drill into a lagging hotel’s Performance Dashboard.',
        'Benefits of learning RoomNexa this way: fewer wrong-hotel postings, cleaner guest folios, accurate rates and taxes, faster check-in, and reports that match what owners expect at the end of the week.'
      ),
      bullets(
        [
          'Create and manage reservations, walk-ins, and room allocations',
          'Check guests in and out and keep the booking calendar accurate',
          'Track room cleanliness, maintenance, and lost & found',
          'Run restaurant and room-service orders (POS) with KOT-style kitchen flow',
          'Control rates, sellable inventory, and OTA / NexaStay distribution',
          'Collect payments, run cash counter, and export operational reports',
        ],
        'What you can do in RoomNexa'
      ),
      callout(
        'note',
        'Who should read this first',
        'Receptionists and cashiers: First Login → Home Dashboard → Walk-in / Check-in / Checkout. Managers: also complete Property Setup before go-live. Org admins: Users, Roles & Permissions plus Organisation Settings.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Active RoomNexa login',
          'You need a user account created by your organisation admin.',
        ],
        [
          'Hotel assignment',
          'Your account must be linked to at least one hotel property on Hotelier.',
        ],
        [
          'Feature permissions (RBAC)',
          'Menus appear only when your role includes the matching common.* permission (for example manage_booking_operations).',
        ],
        [
          'Hotel package / entitlements',
          'POS, Distribution, Dynamic Pricing, and similar modules also require the hotel’s subscribed package to enable those features.',
        ],
      ]),
      bullets(
        [
          'Official RoomNexa URL for your organisation (bookmark it)',
          'Supported modern browser (Chrome, Edge, Safari, or Firefox)',
          'Know which property you work today if you manage more than one hotel',
        ],
        'Prerequisites'
      ),
      callout(
        'important',
        'Permissions vary by role',
        'If a menu item is missing, ask your admin to check Roles & Permissions and the hotel package — do not assume the feature is broken.'
      ),
    ],
    steps: [
      heading('Your first 10 minutes'),
      steps([
        {
          text: 'Open RoomNexa and sign in with the email/username and password provided by your admin. Use Forgot Password only on the official login page if needed.',
        },
        {
          text: 'On the organisation Landing page, open Hotelier. You will see property cards with status chips (Active, Onboarding, Suspended, or Expired).',
        },
        {
          text: 'Select the hotel you will operate today. Expired subscriptions cannot be opened until renewed from Settings → Subscription.',
        },
        {
          text: 'Confirm the property name in the operational header before creating any booking, payment, or POS order.',
        },
        {
          text: 'Open Dashboard to see the room/booking calendar, today’s context, and the Pending Tasks rail.',
        },
        {
          text: 'Scan arrivals and checkouts for the shift, then open Booking or Housekeeping as your role requires.',
        },
        {
          text: 'Bookmark Landing → User Guide so new staff can self-train without hunting for SOPs.',
        },
        {
          text: 'At shift end on shared PCs, log out so the next agent’s actions are audited under their own name.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Hotelier — cards for hotels you can open, with photo, location, property type, star rating, and subscription status',
          'Organisation Dashboard — multi-hotel revenue, expenses, occupancy, sources (needs view_organization_dashboard)',
          'Settings — Users tab and Subscription tab for org admins',
          'User Guide — this documentation portal with Search and Related Guides',
          'Hotel Dashboard — day-to-day calendar and tasks after you open a hotel',
        ],
        'Main landing areas'
      ),
      table(
        ['Area', 'Audience', 'Primary job'],
        [
          ['Hotelier', 'All operational staff', 'Enter the correct property context'],
          ['Org Dashboard', 'Owners / group managers', 'Compare hotels and spot outliers'],
          ['Settings', 'Org admins', 'Users, roles, subscription health'],
          ['Hotel modules', 'FO / HK / F&B / finance', 'Run the guest stay lifecycle'],
        ],
        'Where work happens'
      ),
    ],
    workflow: [
      workflow('Typical hotel day in RoomNexa', [
        'Login',
        'Open hotel from Hotelier',
        'Review Dashboard / Pending Tasks',
        'Process reservations & walk-ins',
        'Check-in arrivals',
        'Housekeeping updates',
        'POS / extras during stay',
        'Checkout & payment',
        'Cash close / night reports',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'RoomNexa separates organisation-level work (users, multi-hotel overview, subscription) from property-level work (bookings, rooms, POS, cash). Opening a hotel sets the operational context: bookings, payments, and reports you create next apply only to that property until you switch.',
        'Feature visibility is controlled by RBAC (role-based access control) and the hotel’s subscribed package. Two users at the same hotel may see different menus. A missing menu is usually a permission or package gap, not a product bug.',
        'Audit trails attribute bookings, payments, cancellations, and many admin changes to the logged-in user. Shared “front desk” logins make refunds and mistakes hard to investigate — use named accounts.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Train by workflow, not by menu',
        'Teach new staff the guest journey (reserve → arrive → stay → pay → leave) before deep configuration screens.'
      ),
      callout(
        'tip',
        'Wrong-hotel check',
        'Say the hotel name out loud when you open the property. Multi-property managers lose more money to wrong context than to rate mistakes.'
      ),
      callout(
        'note',
        'Package modules',
        'POS, Distribution, ChannelHub, and Dynamic Pricing appear only when both role permission and hotel entitlement allow them.'
      ),
    ],
    bestPractices: [
      bullets([
        'Create a shared front-office checklist using Related Guides at the bottom of each page.',
        'Keep one supervisor account for overrides (refunds, large discounts) and limit those permissions.',
        'Use a demo hotel or non-critical dates for training — never practice cancellations on live sold-out nights.',
        'Start every shift on Home Dashboard for two minutes before other modules.',
        'Document property SOPs in the same language as this guide (walk-in, folio, OOO, KOT).',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Working in the wrong hotel after switching properties.',
        'Skipping Property Setup (rooms/rates/tax) and trying to take live bookings.',
        'Assuming missing menus are bugs instead of permission or package gaps.',
        'Leaving shared PCs logged in as the previous shift’s user.',
        'Promising rooms from memory without checking the Dashboard calendar.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot see any hotel',
            'User not assigned to a property',
            'Ask org admin to assign hotels in Settings → Users',
          ],
          [
            'Login works but menus are empty',
            'Role lacks feature permissions or package disabled',
            'Review role permissions and hotel subscription packages with admin',
          ],
          [
            'Page looks different from this guide',
            'Package, role, or deployment difference',
            'Confirm the module is enabled; use Search for the exact screen name',
          ],
          [
            'Cannot open a hotel card',
            'Subscription expired or hotel suspended',
            'Check status chip; renew via Settings → Subscription',
          ],
          [
            'Actions appear under the wrong staff name',
            'Shared login left open',
            'Log out and sign in with the correct named account',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is RoomNexa only for large hotels?',
          a: 'No. Boutique hotels and multi-property groups both use it. Modules scale with your package and roles.',
        },
        {
          q: 'Do I need training before taking a walk-in?',
          a: 'Read First Login, Property Setup (if you configure), then Walk-in Booking and Check-in. You can take a walk-in after rooms and rates exist.',
        },
        {
          q: 'Where do I find help while working?',
          a: 'Open Landing → User Guide, use Search, or jump from Related Guides at the end of any page. Escalate via Support when blocked.',
        },
        {
          q: 'What is the difference between Landing and hotel screens?',
          a: 'Landing is organisation context (Hotelier, org dashboard, settings). After you open a hotel, you enter property operations (bookings, HK, POS, finance).',
        },
        {
          q: 'Why can my colleague see POS but I cannot?',
          a: 'Their role likely includes manage_pos_restaurant and the hotel package enables POS. Ask admin to adjust your role or confirm entitlement.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'first-login',
    title: 'First Login Guide',
    moduleId: 'getting-started',
    summary: 'Sign in safely, choose your hotel, and reach the operational dashboard.',
    keywords: ['login', 'password', 'sign in', 'first login', 'account'],
    pathHint: 'Login → Landing → Hotelier → Dashboard',
    related: ['welcome', 'property-setup', 'users-roles'],
    overview: [
      ...paragraphs(
        'First Login covers authentication and reaching the correct property. Most day-to-day mistakes start here: wrong hotel context, expired or shared passwords, or opening an expired subscription property.',
        'After a successful login you land on the organisation home. From there, Hotelier lists properties assigned to you. Opening a hotel switches RoomNexa into that property’s operational namespace — bookings, payments, and reports you create next belong to that hotel.',
        'Use this guide on day one for every new receptionist, cashier, and supervisor. Managers should also verify that the user’s role shows the expected menus before the first live shift.',
        'Practical example: a multi-property night auditor who logs into “City Boutique” by habit while covering “Airport Inn” will post cash and checkouts to the wrong folio set. Always confirm the hotel name in the header before the first action of the shift.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Active user account',
          'Admin must create your user and leave the account active.',
        ],
        [
          'Hotel assignment',
          'At least one hotel must be linked to your user or you will see an empty Hotelier.',
        ],
        [
          'Role with operational permissions',
          'Without booking/HK/finance permissions, you may login but cannot do floor work.',
        ],
      ]),
      bullets(
        [
          'Login ID and temporary password from your admin',
          'Supported modern browser (Chrome, Edge, Safari, Firefox)',
          'Official RoomNexa URL for your organisation (never reuse unknown links from chat)',
          'Quiet minute to change password and open the correct hotel',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Phishing & OTP safety',
        'Use Forgot Password only on the official login page. Never share OTPs or temporary passwords in public WhatsApp groups.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open the official RoomNexa login page and enter your username/email and password. Prefer a clean browser profile on shared front-desk PCs.',
        },
        {
          text: 'If this is your first login and the system asks you to change password, choose a strong unique password and store it per your hotel policy (password manager or sealed staff envelope — not a sticky note on the monitor).',
        },
        {
          text: 'After login you land on the organisation home. Depending on role you may see Hotelier, Dashboard (org), Settings, Profile, and User Guide.',
        },
        {
          text: 'Open Hotelier and review each property card: name, city, status chip (Active / Onboarding / Suspended / Expired), and renew link if subscription is expiring.',
        },
        {
          text: 'Click the hotel you will work today. Expired cards do not open — renew subscription first or ask an org admin.',
        },
        {
          text: 'Verify the hotel name in the operational header. Optionally toggle light/dark theme from the header for shift comfort.',
        },
        {
          text: 'Open Dashboard. Confirm the calendar shows rooms for this property and that Pending Tasks load for today.',
        },
        {
          text: 'If menus you need are missing, stop and ask admin to adjust Roles & Permissions before taking live guests.',
        },
        {
          text: 'On shared PCs, log out at end of shift. Do not leave a supervisor session open for the next receptionist.',
        },
      ]),
    ],
    screens: [
      heading('Login fields'),
      fieldTable([
        ['Email / Username', 'Yes', 'Identifies your staff account'],
        ['Password', 'Yes', 'Authenticates you; change on first login if prompted'],
        ['Remember me (if shown)', 'No', 'Keeps session on trusted personal devices only — avoid on shared front-desk PCs'],
        ['Forgot password', '—', 'Starts reset flow via the email registered on your user profile'],
      ]),
      heading('After login'),
      bullets(
        [
          'Landing sidebar — Hotelier, Organisation Dashboard, Settings, User Guide, Profile',
          'Hotelier cards — property photo, status chip, renew link, Open action',
          'Hotel header — confirms which property context is active',
          'Theme toggle — light/dark comfort setting in the header area',
        ],
        'UI elements you will use'
      ),
    ],
    workflow: [
      workflow('First-day access', [
        'Receive credentials',
        'Login on official URL',
        'Change password if prompted',
        'Open Hotelier',
        'Select hotel',
        'Confirm header name',
        'Open Dashboard',
        'Verify menus for your role',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Sessions are user-based. Actions such as bookings, payments, cancellations, and many configuration changes are attributed to the logged-in user for audit. That is why shared permanent logins are unsafe for hotels.',
        'If your organisation grants multi-hotel access, switching hotels changes the operational context for all subsequent screens. Data does not “merge” across hotels while you work — you are always in one property context at a time.',
        'Subscription state on Hotelier can block entry. An expired entitlement disables the hotel card until Settings → Subscription is renewed. Onboarding or suspended statuses also signal that the property may not be ready for live operations.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Shift handover',
        'Log out on shared front-desk PCs at end of shift so the next agent’s actions are audited under their own name.'
      ),
      callout(
        'tip',
        'Two-hotel coverage',
        'Finish tasks in Hotel A, return to Landing → Hotelier, then open Hotel B. Prefer one context at a time over parallel browser chaos.'
      ),
      callout(
        'important',
        'Header is truth',
        'If a guest is waiting and you feel rushed, still glance at the hotel name before Save on any booking or payment.'
      ),
    ],
    bestPractices: [
      bullets([
        'Use unique passwords per staff member — never a shared “front desk” login for permanent use.',
        'Enable theme comfort (light/dark) after login so night shifts reduce eye strain.',
        'Keep a sealed break-glass admin credential offline; do not paste it into chat.',
        'Test Forgot Password on day one for at least one supervisor so reset email delivery is proven.',
        'Brief new joiners on which hotel cards they are allowed to open.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Staying logged in as a previous shift’s user.',
        'Entering the wrong hotel when you manage multiple properties.',
        'Practising on a live sold-out date during first login training.',
        'Ignoring an “Expires soon — Renew” link until the morning of expiry.',
        'Saving passwords in a public staff WhatsApp group.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Invalid credentials',
            'Wrong password, inactive user, or typo in username',
            'Retry carefully; use Forgot Password; ask admin to unlock/reset',
          ],
          [
            'Blank landing after login',
            'Network, cache, or transient app error',
            'Hard refresh; try another browser; contact support if it persists',
          ],
          [
            'Cannot open hotel',
            'Subscription expired, suspended status, or not assigned',
            'Check status chip and assignment; renew subscription if Expired',
          ],
          [
            'Forgot Password email never arrives',
            'Wrong email on profile, spam filter, or mail delay',
            'Check spam; confirm email with admin; retry after a few minutes',
          ],
          [
            'Menus missing after open',
            'Role lacks permissions or package feature off',
            'Admin reviews role + hotel enabled features',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can two people use one account?',
          a: 'Technically possible but not recommended. Audits, refunds, and mistakes become untraceable.',
        },
        {
          q: 'What if Forgot Password email never arrives?',
          a: 'Check spam, confirm the email on your user profile with admin, then retry. Admin can also reset credentials.',
        },
        {
          q: 'Do I need manage_configuration to login?',
          a: 'No. Login only needs an active user. Configuration permission is for property master-data setup.',
        },
        {
          q: 'Can I stay logged in overnight?',
          a: 'On a personal device maybe; on shared front-desk PCs always log out. Sessions left open create audit and security risk.',
        },
        {
          q: 'Where do I change my profile details?',
          a: 'Use Profile from the Landing area. Ask admin if you need email or hotel assignment changes you cannot edit yourself.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'property-setup',
    title: 'Property Setup Guide',
    moduleId: 'getting-started',
    summary:
      'Recommended order to configure a new hotel before taking live bookings.',
    keywords: ['setup', 'onboarding', 'configuration', 'rooms', 'rates', 'go live'],
    pathHint: 'Hotel → Configuration',
    related: ['basic-details', 'room-types', 'rooms', 'rate-plans', 'finance-setup'],
    videos: [
      video(
        'dclrvWcH4p4',
        'Configure your property in minutes',
        'See how to set up property masters in Roomnexa before go-live.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Before the front desk takes walk-ins or OTA bookings, property master data must exist: hotel identity, locale, floors, room types, rooms, rate plans, taxes, and invoice branding. Skipping this sequence is the most common reason new properties see empty calendars, zero rates, or broken invoices.',
        'This guide is the recommended go-live order used by RoomNexa properties. Follow it once for a new hotel, then use individual Configuration guides for day-to-day edits.',
        'Managers and configuration admins own this work. Front-office staff should not invent room numbers or tax settings on the fly during a busy arrival — escalate to someone with manage_configuration.',
        'Example: a 24-room boutique opening next week needs Basic Details and Config (currency/timezone) first, then Floors → Room Types → Rooms, then Rate Plans and Manage Rate for the opening month, then Finance Setup (GST) and Invoice Setup, then Users for FO/HK roles, then a test walk-in on a quiet date.',
        'When the sequence is complete, Home Dashboard shows sellable rooms, Quick Booking and Walk-in can quote rates, and invoices carry the correct tax and branding.'
      ),
      callout(
        'success',
        'Go-live checklist goal',
        'Dashboard calendar shows rooms, Manage Rate has non-zero prices for sell dates, tax rules exist, and a test walk-in → check-in → checkout completes without configuration errors.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        ['manage_configuration', 'Required to edit property master data'],
        ['Hotel admin or equivalent role', 'Usually limited to managers'],
        ['manage_users (recommended)', 'Needed to create FO/HK/F&B users after masters exist'],
        ['manage_finance (for rates/tax)', 'Often required for Manage Rate and Finance Setup'],
      ]),
      bullets(
        [
          'Legal hotel name, address, phone, email, and logo assets',
          'GSTIN / tax details (for India GST hotels) and invoice footer text',
          'Floor plan and final door room numbers',
          'Room type list with adult/child capacities and booking mode',
          'Base tariffs, meal plans (for example EP/CP/MAP), and seasonal dates',
          'Staff list with intended roles (FO, HK, cashier, manager)',
        ],
        'Have these ready'
      ),
      callout(
        'warning',
        'Do not connect OTAs too early',
        'Freeze room type names and rates before Distribution mapping. Remapping after go-live causes allotment mismatches.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open the hotel from Hotelier, then go to Configuration → Basic Details. Save legal name, contacts, address, and identity fields used on guest-facing and invoice surfaces.',
        },
        {
          text: 'Set Config / locale settings: timezone, country, currency, and date format. Wrong timezone or currency breaks night figures and rate displays.',
        },
        {
          text: 'Create Floors that match the building (Ground, 1st, etc.), then Room Types with capacity and booking mode.',
        },
        {
          text: 'Create Rooms assigned to a room type and floor. Use the same numbers printed on doors (101, 102). Mark rooms active when sellable.',
        },
        {
          text: 'Create Rate Plans, then verify daily prices under Finance → Manage Rate for the go-live date range and meal plans you sell.',
        },
        {
          text: 'Complete Finance Setup (tax/GST rules) and Invoice Setup (logo, branding, footer). Run a sample invoice preview if available.',
        },
        {
          text: 'Add Amenities, Images, and Terms & Policy so NexaStay/guest-facing content and FO policy times stay consistent.',
        },
        {
          text: 'Create hotel Users with least-privilege roles for front office, housekeeping, and F&B. Avoid giving every staff member owner-level access.',
        },
        {
          text: 'Optional modules: enable Communications, POS menu/tables, Distribution mapping, and Dynamic Pricing only after core FO path works.',
        },
        {
          text: 'Test one walk-in + check-in + checkout (and a POS order if enabled) on a non-critical date before marketing go-live. Fix gaps, then brief the shift.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Configuration hub — lists Basic Details, Room Types, Floors, Rooms, Rate Plan, Amenities, Images, Terms, Finance Setup, Invoice Setup, Users, Communications',
          'Save actions — write immediately to the selected hotel; refresh Dashboard after major inventory changes',
          'Manage Rate — date-wise tariffs per room type / plan (Finance)',
          'Hotelier status — Onboarding vs Active reminds you the property may still be incomplete',
        ],
        'Screens and controls'
      ),
      fieldTable([
        ['Basic Details', 'Yes', 'Hotel identity and contacts used across ops and documents'],
        ['Timezone / currency', 'Yes', 'Keeps rates, reports, and night boundaries consistent'],
        ['Floors', 'Yes', 'Groups rooms for HK and allocation'],
        ['Room Types', 'Yes', 'Sellable categories with capacity'],
        ['Rooms', 'Yes', 'Individual inventory units linked to type + floor'],
        ['Rate Plans + Manage Rate', 'Yes', 'What FO quotes and OTAs/NexaStay sell'],
        ['Finance / Invoice Setup', 'Yes for live billing', 'Tax rules and invoice branding'],
      ]),
    ],
    workflow: [
      workflow('Setup sequence', [
        'Basic Details',
        'Locale Config',
        'Floors',
        'Room Types',
        'Rooms',
        'Rate Plans + Manage Rate',
        'Taxes & Invoice',
        'Amenities / Images / Terms',
        'Users & roles',
        'Test booking path',
        'Then Distribution / POS (optional)',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Rooms cannot sell until they exist as inventory units linked to a room type. The Home Dashboard calendar and Quick Booking read this inventory. Empty calendars almost always mean rooms were never created or are inactive.',
        'Rate plans and Manage Rate drive quoted prices. Tax rules from Finance Setup apply at billing time. Invoice Setup controls how PDFs and printed bills look to guests and auditors.',
        'Distribution and OTA mapping should happen only after room types and rates are stable. Changing room type structure after channels are mapped often causes allotment and availability desync. POS and Stock are optional for FO go-live — many hotels open rooms first, F&B later.',
        'User roles gate who can change masters after go-live. Keep tax, invoice, and rate edit rights narrower than walk-in rights.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Name rooms the way staff say them',
        'Use the same room numbers printed on doors (101, 102). Avoid internal codes that confuse housekeeping and night audit.'
      ),
      callout(
        'tip',
        'One test stay',
        'A single complete test stay catches missing tax, zero rates, and broken PDF pop-ups before real guests arrive.'
      ),
      callout(
        'note',
        'POS is optional at open',
        'Front office can run on Booking + Housekeeping alone. Add Restaurant/POS when menu and tables are ready.'
      ),
    ],
    bestPractices: [
      bullets([
        'Freeze room type names before connecting OTAs or NexaStay mapping.',
        'Document check-in/out times in Terms & Policy and train staff to the same times.',
        'Keep a manager-only role for tax, invoice, and bulk rate changes.',
        'Load at least 30–90 days of Manage Rate before opening online channels.',
        'Photograph rooms after setup and upload under Images for direct-booking quality.',
        'Re-check Housekeeping Room Status after creating rooms so FO knows clean/dirty defaults.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Creating bookings before rooms exist.',
        'Duplicate room numbers across floors.',
        'Leaving currency/timezone wrong — breaks reports and night figures.',
        'Publishing OTA mappings while rates are still zero.',
        'Giving every staff member manage_configuration “just in case”.',
        'Skipping the test checkout and discovering GST issues on opening weekend.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'No rooms on calendar',
            'Rooms not created or inactive',
            'Create Rooms under Configuration; ensure they are active and linked to a type',
          ],
          [
            'Rate shows zero / cannot quote',
            'Rate plan or Manage Rate missing for dates',
            'Create Rate Plan and set Manage Rate for the date range and meal plan',
          ],
          [
            'Tax missing on invoice',
            'Finance Setup incomplete',
            'Complete tax/GST rules in Finance Setup and re-test a bill',
          ],
          [
            'PDF invoice blocked',
            'Browser pop-up blocker',
            'Allow pop-ups for RoomNexa; retry download',
          ],
          [
            'OTA availability wrong after setup',
            'Mapping done on unstable room types',
            'Re-check Distribution → Channel Mapping and inventory sync',
          ],
          [
            'Staff cannot see Configuration',
            'Missing manage_configuration',
            'Assign a manager role with configuration permission',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I change room types after OTA mapping?',
          a: 'Yes, but re-check Distribution → Channel Mapping immediately or online availability will desync.',
        },
        {
          q: 'Do I need POS setup to open the hotel?',
          a: 'No. POS is optional. Front office can run on Booking + Housekeeping alone.',
        },
        {
          q: 'Who should perform Property Setup?',
          a: 'A manager or implementer with manage_configuration (and usually finance permissions for rates/tax). Not every receptionist.',
        },
        {
          q: 'When should I add Users?',
          a: 'After masters exist so you can verify each role against real screens. Create least-privilege FO/HK roles before go-live marketing.',
        },
        {
          q: 'Is Organisation Settings part of property setup?',
          a: 'Organisation Settings covers subscription and org users. Property masters live under the hotel’s Configuration after you open the hotel.',
        },
        {
          q: 'What if the hotel card still says Onboarding?',
          a: 'Finish required setup and follow your organisation’s activation process. Staff may still configure while status shows Onboarding, but treat live selling cautiously until Active.',
        },
      ]),
    ],
  }),
];
